import { CheckCircleOutlined, DollarOutlined, SwapOutlined } from '@ant-design/icons';
import type { OrderHistoryEntry, OrderStatus } from '@shop-monorepo/types';
import { Empty, Spin, Timeline, Typography } from 'antd';
import React from 'react';
import styled from 'styled-components';

import { OrderStatusTag } from '../../components/OrderStatusTag';
import { useOrderHistory } from '../../hooks/useOrderQueries';

const { Text } = Typography;

const HistoryWrapper = styled.div`
  padding: 4px 0;
  margin-left: 10px;
`;

const EntryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EntryMeta = styled(Text)`
  && {
    font-size: 12px;
  }
`;

const StatusChange = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

interface OrderHistoryProps {
  orderId: number;
}

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('uk-UA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const renderStatusChange = (entry: OrderHistoryEntry) => {
  return (
    <EntryContent>
      <div>
        <StatusChange>
          {entry.oldValue && (
            <>
              <OrderStatusTag status={entry.oldValue as OrderStatus} />
              {' \u2192 '}
            </>
          )}
          <OrderStatusTag status={entry.newValue as OrderStatus} />
        </StatusChange>
      </div>
      <EntryMeta type="secondary">
        {entry.userName ?? 'Система'} &middot; {formatDate(entry.createdAt)}
      </EntryMeta>
    </EntryContent>
  );
};

const renderPaymentChange = (entry: OrderHistoryEntry) => {
  const oldLabel = entry.oldValue === 'true' ? 'Оплачено' : 'Не оплачено';
  const newLabel = entry.newValue === 'true' ? 'Оплачено' : 'Не оплачено';

  return (
    <EntryContent>
      <div>
        {oldLabel} {'\u2192'} {newLabel}
      </div>
      <EntryMeta type="secondary">
        {entry.userName ?? 'Система'} &middot; {formatDate(entry.createdAt)}
      </EntryMeta>
    </EntryContent>
  );
};

const renderCashChange = (entry: OrderHistoryEntry) => {
  const oldLabel = entry.oldValue === 'true' ? 'Проведено' : 'Не проведено';
  const newLabel = entry.newValue === 'true' ? 'Проведено' : 'Не проведено';

  return (
    <EntryContent>
      <div>
        {oldLabel} {'\u2192'} {newLabel}
      </div>
      <EntryMeta type="secondary">
        {entry.userName ?? 'Система'} &middot; {formatDate(entry.createdAt)}
      </EntryMeta>
    </EntryContent>
  );
};

export const OrderHistory: React.FC<OrderHistoryProps> = ({ orderId }) => {
  const { data: history, isLoading } = useOrderHistory(orderId);

  if (isLoading) {
    return <Spin size="small" />;
  }

  if (!history || history.length === 0) {
    return <Empty description="Немає записів" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const getIcon = (action: string) => {
    if (action === 'status_change') return <SwapOutlined style={{ fontSize: 16 }} />;
    if (action === 'cash_change') return <CheckCircleOutlined style={{ fontSize: 16 }} />;
    return <DollarOutlined style={{ fontSize: 16 }} />;
  };

  const getContent = (entry: OrderHistoryEntry) => {
    if (entry.action === 'status_change') return renderStatusChange(entry);
    if (entry.action === 'cash_change') return renderCashChange(entry);
    return renderPaymentChange(entry);
  };

  const items = history.map((entry) => ({
    key: entry.id,
    icon: getIcon(entry.action),
    content: getContent(entry),
  }));

  return (
    <HistoryWrapper>
      <Timeline items={items} />
    </HistoryWrapper>
  );
};
